import { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import { XGrid } from '@AMIEWEB/Common/XGrid/XGrid';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Grid } from '@mui/material';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { IPets, gridTags } from 'app/models/Placement/PlacementMods';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentModDetails,
  selectEditPreferencePetsRows,
  selectExpandedModsDialog,
  selectIsEditExistingMod,
  selectIsEditMod,
  selectIsUndoModal,
  selectPreferencePetsTypeError,
} from 'store/redux-store/placement-mods/selectors';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { makeStyles } from 'tss-react/mui';
import { generateRandomId, getPetsGridColumns } from './utils';
import { GridSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';

const expandedDetailPanelStyles = makeStyles()({
  expandedGrid: {
    fontSize: '14px',
    '& .MuiDataGrid-main': {
      minHeight: '60px',
    },
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  isErrorCell: {
    border: '1px solid !important',
    borderColor: 'red',
    color: 'red',
  },
});

export const PetsGrid = ({ petsTableInfo }: { petsTableInfo: IPets[] }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //*START - SELECTORS
  const isExpanded = useSelector(selectExpandedModsDialog);
  const isModEdit = useSelector(selectIsEditMod);
  const selectedModsDetails = useSelector(selectCurrentModDetails);
  const isUndoModal = useSelector(selectIsUndoModal);
  const isEditExistingMod = useSelector(selectIsEditExistingMod);

  //Grid Specific slectors
  const editPreferencePetsRequests = useSelector(selectEditPreferencePetsRows);
  const preferencePetsTypeError = useSelector(selectPreferencePetsTypeError);
  //*END - SELECTORS
  const { classes } = expandedDetailPanelStyles();
  const [headerCount, setHeaderCount] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [columns, setColumns] = useState<any>(
    getPetsGridColumns(t, isExpanded, isModEdit, classes, editPreferencePetsRequests),
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
  const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);

  const { promiseInProgress: isCallingAPI } = usePromiseTracker({ area: 'preferences-details-api', delay: 0 });

  //SET COLUMNS
  useEffect(() => {
    const initialColumns = getPetsGridColumns(t, isExpanded, isModEdit, classes, editPreferencePetsRequests);
    setColumns(initialColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, isExpanded, isModEdit, editPreferencePetsRequests]);

  //HANDLES THE ROW SELECTION,ENABLE/DISABLE DELETE BUTTON
  const onRowsSelectionHandler = ids => {
    setSelectedIds(ids);
    const modData = isModEdit ? editPreferencePetsRequests : petsTableInfo;
    const selectedRowsData = ids.map(id => modData.find(row => row.id === id));
    selectedRowsData.length > 0 ? setIsDeleteDisabled(false) : setIsDeleteDisabled(true);
  };

  //DELETE SELECTED ROWS,UPDATES EDIT DATA STATE , ERRORS , MOD ALTERED STATUS
  const deleteHandler = () => {
    const modData = isModEdit ? editPreferencePetsRequests : petsTableInfo;
    const updatedDataAfterDelete = modData.filter(item => !selectedIds.includes(item.id));
    const updateTypeError = preferencePetsTypeError.filter(item => !selectedIds.includes(item.id));

    dispatch(placementModsAction.setPetsGridModAltered(true));
    dispatch(placementModsAction.setPreferencePetsTypeError(updateTypeError));
    dispatch(placementModsAction.setEditPetsRows(updatedDataAfterDelete));

    setSelectedIds([]);
  };

  //ADD A NEW ROW,UPDATES EDIT DATA STATE , ERRORS , MOD ALTERED STATUS
  const addNewPetRow = e => {
    const existingRow = editPreferencePetsRequests?.slice(0);
    const adultTypeError = preferencePetsTypeError?.slice(0);

    const newRow = {
      id: generateRandomId(),
      type: '',
      breed: '',
      weight: null,
      details: '',
    };
    existingRow.unshift(newRow);

    const typeError = {
      type: true,
      id: newRow?.id,
    };

    adultTypeError.unshift(typeError);
    dispatch(placementModsAction.setPreferencePetsTypeError(adultTypeError));
    dispatch(placementModsAction.setPetsGridModAltered(true));
    dispatch(placementModsAction.setEditPetsRows(existingRow));
  };
  //REFRESH GRID DATA
  const handleRefreshGrid = () => {
    dispatch(placementModsAction.setPetsGridModError(false));
    dispatch(
      placementModsAction.getPreferencesModDetailAction({
        placementId: selectedModsDetails.placementId,
        travelerOptionId: selectedModsDetails.travelerOptionId,
        newMod: !isEditExistingMod,
      }),
    );
  };

  //RESET COLUMN
  const resetColumns = () => {
    setColumns(getPetsGridColumns(t, isExpanded, isModEdit, classes, editPreferencePetsRequests));
  };

  //ADD/DELETE ACTION
  const rightActions = React.useMemo<IXGridBulkActionButton[]>(() => {
    return [
      {
        key: 'add-new-petInfo',
        Icon: AddIcon,
        disabled: !isModEdit,
        onClick: e => {
          addNewPetRow(e);
        },
        tooltipProps: {
          tooltipContent: 'Add Pet',
        },
      },
      {
        key: 'delete',
        Icon: DeleteOutlinedIcon,
        disabled: !selectedIds || selectedIds?.length === 0,
        onClick: () => deleteHandler(),
        tooltipProps: {
          tooltipContent: 'Delete',
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, isModEdit, isDeleteDisabled, selectedIds, editPreferencePetsRequests, preferencePetsTypeError]);

  //GET HEADER COUNT METHOD
  const getHeaderCount = rows => {
    const totalRowCount = rows?.length;
    const nullRowCount = rows?.filter(each => each?.type === '' || each?.type === null)?.length;
    setHeaderCount(totalRowCount - nullRowCount);
  };

  //SET GRID ERROR FOR NO DATA,UPDATE HEADER COUNT
  useEffect(() => {
    if (isModEdit) {
      setSortModel([]);
    }
    getHeaderCount(isModEdit ? editPreferencePetsRequests : petsTableInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPreferencePetsRequests]);

  // UNDO ACTION
  useEffect(() => {
    if (isUndoModal) {
      dispatch(placementModsAction.setPreferencePetsTypeError([]));
      dispatch(placementModsAction.setPetsGridModError(false));
      dispatch(placementModsAction.setIsUndoModal(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUndoModal]);

  //SET  CELL ERRORS
  useEffect(() => {
    if (preferencePetsTypeError?.some(val => val.type)) {
      dispatch(placementModsAction.setPetsGridModError(true));
    } else {
      dispatch(placementModsAction.setPetsGridModError(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferencePetsTypeError, editPreferencePetsRequests]);

  const sortChangeHandler = model => {
    setSortModel(model);
  };

  const PetsInfoGrid = React.useMemo(() => {
    return (
      <Grid>
        <XGrid
          components={{
            NoRowsOverlay: () => {
              return (
                <div className="center" style={{ height: '100%' }}>
                  {t('placement.placementMods.noRecordsFound')}
                </div>
              );
            },
          }}
          gridTag={gridTags.PETSGRIDTAG}
          rows={isModEdit ? editPreferencePetsRequests || [] : petsTableInfo || []}
          columns={columns}
          disableSelectionOnClick
          pagination={false}
          autoHeight={true}
          sx={{ minWidth: '500px' }}
          sortModel={sortModel}
          onSortModelChange={sortChangeHandler}
          hideFooter
          editMode="row"
          inDialog
          title={t('placement.placementMods.pets')}
          loading={isCallingAPI}
          displayCustomCount={selectedIds?.length > 0 ? selectedIds?.length : headerCount}
          className={classes.expandedGrid}
          checkboxSelection={isModEdit}
          selectionModel={selectedIds}
          onSelectionModelChange={ids => onRowsSelectionHandler(ids)}
          toolbarProps={{
            refreshButton: true,
            rightActions: rightActions,
            columnSelector: isModEdit ? false : true,
            columnSelectorProps: {
              fallbackPlacements: [
                'bottom-end',
                'bottom',
                'bottom-start',
                'top-start',
                'top',
                'top-end',
                'left',
                'right',
              ],
            },
            resetColumns: () => {
              resetColumns();
              dispatch(
                globalActions.setSnackBar({
                  message: 'Columns have been reset.',
                  severity: 'success',
                }),
              );
            },
            refreshGrid: () => {
              handleRefreshGrid();
            },
          }}
        />
      </Grid>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petsTableInfo, isExpanded, isCallingAPI, resetColumns, columns, sortModel, editPreferencePetsRequests]);

  return <>{PetsInfoGrid}</>;
};
